import { ApolloError } from '@apollo/client';

// Per-record channel on the Communication model (each row is EMAIL or SMS) —
// distinct from DeliveryOption, the user's per-send input choice.
export enum CommunicationMethod {
  EMAIL = 'EMAIL',
  SMS = 'SMS'
}

// The user's per-send input choice (email, SMS, both, or preferred-with-fallback);
// mirrors the server's DeliveryOption enum.
export enum DeliveryOption {
  EMAIL_ONLY = 'EMAIL_ONLY',
  SMS_ONLY = 'SMS_ONLY',
  EMAIL_PREFERRED = 'EMAIL_PREFERRED',
  SMS_PREFERRED = 'SMS_PREFERRED',
  BOTH = 'BOTH'
}

export const DELIVERY_OPTION_LABELS: Record<DeliveryOption, string> = {
  [DeliveryOption.EMAIL_ONLY]: 'Email only',
  [DeliveryOption.SMS_ONLY]: 'SMS only',
  [DeliveryOption.EMAIL_PREFERRED]: 'Email preferred (SMS fallback)',
  [DeliveryOption.SMS_PREFERRED]: 'SMS preferred (email fallback)',
  [DeliveryOption.BOTH]: 'Both email and SMS'
};

// Target axis of an ad-hoc send — the entity the send is about. Mirrors the
// server's CommunicationContextType enum; ASSESSMENT is the implicit root.
export enum CommunicationContextType {
  ASSESSMENT = 'ASSESSMENT',
  ASSESSMENT_SESSION = 'ASSESSMENT_SESSION',
  AGE_GROUP = 'AGE_GROUP',
  PLAYER_ASSESSMENT = 'PLAYER_ASSESSMENT'
}

// Receiver axis of an ad-hoc send — who each rendered copy is for. Mirrors the
// server's CommunicationReceiverType enum; single member until the axis grows.
export enum CommunicationReceiverType {
  PLAYER_CONTACT = 'PLAYER_CONTACT'
}

// One entry of the server-owned variable registry (exactly one of targetType /
// receiverType set). Substitution is backend-only; the client lists + inserts.
export interface ICommunicationVariable {
  key: string;
  label: string;
  example?: string;
  targetType?: CommunicationContextType;
  receiverType?: CommunicationReceiverType;
  requiresFallback: boolean;
}

// Client-only compose-picker row kinds. Level/Team are value-based criteria
// dimensions — they never become a contextType on the wire.
export enum ComposeContextValueKind {
  LEVEL = 'LEVEL',
  TEAM = 'TEAM'
}

// Row kinds a compose context chip can hold. ASSESSMENT is the implicit root
// and never renders as a row; id-based kinds reuse CommunicationContextType.
export type ComposeContextRowType =
  | CommunicationContextType.AGE_GROUP
  | CommunicationContextType.ASSESSMENT_SESSION
  | CommunicationContextType.PLAYER_ASSESSMENT
  | ComposeContextValueKind;

// Row kinds that reference an entity instance by id (everything but Level/Team).
export type ComposeContextIdKind = Exclude<ComposeContextRowType, ComposeContextValueKind>;

// One selected context row. id-based rows carry `id`, value-based rows carry
// `value`; `label` is stored so restored draft chips render after instance deletion.
export interface IComposeContextSelection {
  type: ComposeContextRowType;
  id?: string;
  value?: string;
  label: string;
}

// A page's lightweight "compose from here" reference — id-kind only; the panel
// resolves it to a labeled IComposeContextSelection scoped to its own assessment.
export interface IComposeContextRef {
  type: ComposeContextIdKind;
  id: string;
}

// The compose form's authored content surfaces, exactly as typed — consumers
// apply delivery-option channel gating when building wire AdHocContentInput.
export interface IComposeAuthoredContent {
  subject: string;
  bodyHtml: string;
  smsBody: string;
}

// The compose form's query-facing fields as one value — published by the form
// owner to the shared store that count/preview consumers read.
export interface IComposeAudienceSelection {
  contexts: IComposeContextSelection[];
  audience: IAdHocAudience;
  deliveryOption: DeliveryOption;
  content: IComposeAuthoredContent;
}

// Mirrors server's AudienceCriteria input type. Empty = assessment-wide send;
// every present, non-empty dimension ANDs (values within a dimension OR).
export interface IAdHocAudience {
  ageGroupIds?: string[];
  playerAssessmentIds?: string[];
  sessionId?: string;
  levels?: string[];
  teams?: string[];
}

export interface IAdHocCommunicationInput {
  assessmentId: string;
  deliveryOption: DeliveryOption;
  title: string;
  subject?: string;
  body?: string;
  bodyHtml?: string;
  smsBody?: string;
  recipientCriteria: IAdHocAudience;
  // Most-specific selected target context; ASSESSMENT is the implicit root.
  contextType?: CommunicationContextType;
  contextId?: string;
  // Test send — when true the message goes to the typed-in testEmails/testPhones
  // instead of the resolved audience, and the send is flagged as a test in history.
  test?: boolean;
  testEmails?: string[];
  testPhones?: string[];
  // Exemplar receiver a test send renders receiver-scoped tokens from; required
  // by the server iff the content uses receiver-scoped tokens.
  testSourceType?: CommunicationReceiverType;
  testSourceId?: string;
}

// Result of the sendAdHocCommunication mutation. Real sends report `queued` (+
// group id); test sends dispatch inline and report the live `sent` tally.
export interface IAdHocCommunicationResult {
  sent: number;
  skipped: number;
  failed: number;
  queued: number;
  communicationGroupId?: string | null;
}

export interface IAdHocCommunicationHistoryItem extends ICommunication {
  subject?: string;
  communicationGroupId?: string;
  attempts?: number;
}

export interface IAdHocSendEvent {
  // `_id` is the event's communicationGroupId — a virtual grouping of the
  // Communication rows sharing it; resend mutations take this value directly.
  _id: string;
  createdAt?: string;
  title?: string;
  subject?: string;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  // In-flight queue counts: `queuedCount` awaits pickup, `pendingCount` is
  // mid-delivery. The event is unsettled (final tally withheld) while their sum > 0.
  queuedCount: number;
  pendingCount: number;
  // Channel roll-up from the server (any EMAIL row / any SMS row). Recipient
  // rows are loaded on demand via adHocSendEventItems, not carried on the event.
  hasEmail: boolean;
  hasSms: boolean;
  // True when this send was a test (delivered to typed-in addresses). Hidden
  // from history by default; surfaced behind the "show test sends" toggle.
  isTest?: boolean;
  // Present only when the send was anchored to an ice time; every recipient
  // row in the event shares it, so it lives on the event, not the rows.
  assessmentSession?: IAssessmentSessionSummary;
}

// Return shape of useCountAdHocRecipients. Lives here (not in the service file)
// so consumers can type props/state without pulling in the Apollo-hook surface.
export interface ICountAdHocRecipientsResult {
  // Distinct normalized addresses that will receive at least one message.
  recipients: number;
  // Total messages dispatched (one per player × address); exceeds `recipients`
  // when someone receives messages for multiple players (the footer asterisk).
  messages: number;
  // Players with usable contact on a different channel than the chosen
  // deliveryOption — only nonzero for *_ONLY options.
  unreachable: number;
  loading: boolean;
  error?: ApolloError;
}

// A "recipient" is a PlayerAssessment — one rendered body per player, delivered
// to all of that player's addresses. The count footer still counts addresses.
export interface IAdHocRecipientSummary {
  playerAssessmentId: string;
  playerName: string;
  ageGroupName?: string;
}

export interface IAdHocRecipientPage {
  items: IAdHocRecipientSummary[];
  total: number;
}

// Distinct level/team strings across the assessment's players, for the pickers.
export interface IAdHocContextValues {
  levels: string[];
  teams: string[];
}

// Mirrors server's AdHocContentInput — the compose-form content a preview renders.
export interface IAdHocContentInput {
  subject?: string;
  body?: string;
  bodyHtml?: string;
  smsBody?: string;
}

export type AdHocTokenIssue = 'UNKNOWN' | 'MISSING_FALLBACK';

// One SMS-preview part; `issue` marks a still-unresolved token so the bubble
// can highlight it without the client owning any tokenizer.
export interface IAdHocSmsSegment {
  text: string;
  issue?: AdHocTokenIssue;
}

// Validation tiers computed server-side; sources the compose form's token errors.
export interface IAdHocTokenReport {
  unknown: string[];
  missingFallback: string[];
  splitTokens: string[];
  blanks: string[];
  usesReceiverScopedTokens: boolean;
}

// Server-rendered preview for one recipient. Recipient + rendered fields are
// null on an empty audience or when the requested recipient fell out of it.
export interface IAdHocCommunicationPreview {
  playerAssessmentId?: string;
  playerName?: string;
  subject?: string;
  bodyHtml?: string;
  smsBody?: string;
  smsSegments: IAdHocSmsSegment[];
  report: IAdHocTokenReport;
}

// Lightweight rows for the compose context pickers — deliberately narrower than
// the ambient IAgeGroup/IAssessmentSession, which require unfetched aggregates.
export interface IComposeContextAgeGroup {
  _id: string;
  name: string;
}

export interface IComposeContextSession {
  _id: string;
  location?: string;
  area?: string;
  date: string;
  start: number;
  ageGroup?: IComposeContextAgeGroup;
}
